import { DonationStatus, DonationTabs, DonationTypeLabel, queryClient, TableTitles } from "@/constants";
import { useDisclosure } from "@/hooks";
import { useUpdateDonationAmount } from "@/hooks/query-hooks/useDonors";
import { Breadcrumb, Button, Divider, Drawer, Form, InputNumber } from "antd";
import { useLocation } from "react-router";
import { MeditationRetreatsTable } from "../MeditationRetreats";
import { DonationTable } from "./DonationTable";

const DontationPathnames = {
    "/padetha-donors": DonationTabs.padetha_donors,
    "/alms-donors": DonationTabs.alms_donors,
    "/fund-donors": DonationTabs.founding_donors,
}

export function AcceptDonationDrawer({
    donation,
}: {
    donation: Donation,
}) {

    const searchParams = useLocation().search;
    const activeTab = new URLSearchParams(searchParams).get("tab") || DonationTabs.alms_donors;

    const pathname = useLocation().pathname;
    const { donationType } = donation;
    const [opened, { toggle }] = useDisclosure(false);

    const retreatRenderColumnKeys = [
        TableTitles.retreat_start_month,
        TableTitles.retreat_name,
        TableTitles.retreat_level,
        TableTitles.retreat_duration,
        TableTitles.retreat_start_date,
        TableTitles.retreat_end_date,
    ]

    const donorRenderColumnKeys = [
        TableTitles.donor_name,
        TableTitles.donor_phone,
        TableTitles.donation_type,
        TableTitles.donation_payment_method,
        TableTitles.donor_address,
        TableTitles.donation_date
    ]

    const [form] = Form.useForm();

    const { mutate, isMutating } = useUpdateDonationAmount();
    const onFinish = (values: { amount: number }) => {
        const payload = {
            id: donation.id,
            amount: values.amount
        };

        mutate(payload, {
            onSuccess: () => {
                toggle();
                queryClient.invalidateQueries({
                    queryKey: ['donations']
                })
            }
        })
    };

    const getTitle = () => {
        switch (DontationPathnames[pathname as keyof typeof DontationPathnames] || activeTab) {
            case DonationTabs.alms_donors || activeTab === DonationTabs.alms_donors:
                return "ဆွမ်း အလှူရှင်များ";
            case DonationTabs.founding_donors || activeTab === DonationTabs.founding_donors:
                return "မတည်‌‌ငွေ အလှူရှင်များ";
            case DonationTabs.padetha_donors || activeTab === DonationTabs.padetha_donors:
                return "ပဒေသာပင် အလှူရှင်များ";
        }
    };

    const isPadethaDonors = DontationPathnames[pathname as keyof typeof DontationPathnames] === DonationTabs.padetha_donors || activeTab === DonationTabs.padetha_donors;

    return (
        <>
            <Button
                type="link"
                size="small"
                onClick={toggle}
            >
                {
                    donation.status === DonationStatus.PENDING ? "Accept" : "Edit"
                }
            </Button>
            <Drawer
                open={opened}
                onClose={toggle}
                placement="right"
                closeIcon={false}
                destroyOnClose
                width={"79vw"}
                footer={
                    <div
                        className="py-2 flex justify-end gap-4"
                    >
                        <Button
                            type="text"
                            className={"!font-poppins"}
                            size="large"
                            onClick={toggle}
                            disabled={isMutating}
                        >
                            Cancle
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            form="accept-donation-form"
                            danger
                            className={"!text-white !font-poppins bg-primary-gradient "}
                            size="large"
                            shape="round"
                            loading={isMutating}
                        >
                            Confirm
                        </Button>
                    </div>
                }
                maskClosable={!isMutating}
            >
                <div
                    className="space-y-6"
                >
                    <Breadcrumb
                        separator="/"
                        className="!mb-4"
                    >
                        <Breadcrumb.Item
                            onClick={toggle}
                            className="cursor-pointer"
                        >
                            <p
                                className="text-base font-semibold"
                            >
                                {getTitle()}
                            </p>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item
                            className="font-poppins text-base"
                        >
                            Reg ID - {donation?.id}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                    <Divider />

                    <div
                        className="p-4 shadow rounded"
                    >
                        <h2
                            className="text-base font-semibold"
                        >
                            {
                                isPadethaDonors
                                    ? "လှူဒါန်းလိုသော ပဒေသာပင်"
                                    : "လှူဒါန်းလိုသော တရားစခန်း အချက်အလက်"
                            }
                        </h2>
                        <Divider />
                        {
                            donation.meditationEvent
                                && !isPadethaDonors ? (
                                <MeditationRetreatsTable
                                    renderColumnKeys={retreatRenderColumnKeys}
                                    dataSource={[donation.meditationEvent!]}
                                    pagination={false}
                                    hasFilter={false}
                                />
                            ) : (
                                <p className="text-base px-4 py-2.5 rounded-lg bg-2C text-white w-fit">
                                    {DonationTypeLabel[donationType]}
                                </p>
                            )
                        }
                    </div>

                    <div
                        className="p-4 shadow rounded"
                    >
                        <h2
                            className="text-base font-semibold"
                        >
                            အလှူရှင် အချက်အလက်
                        </h2>
                        <Divider />
                        <DonationTable
                            renderColumnKeys={donorRenderColumnKeys}
                            dataSource={[donation]}
                            pagination={false}
                        />
                    </div>

                    <Form
                        layout="vertical"
                        className="w-full"
                        initialValues={{
                            amount: donation.amount || 0,
                        }}
                        onFinish={onFinish}
                        form={form}
                        id="accept-donation-form"
                        name="accept-donation-form"
                        autoComplete="off"
                    >
                        <Form.Item
                            label="လှူဒါန်းမည့် ပမာဏ"
                            name="amount"
                            rules={[
                                { required: true, message: 'လှူဒါန်းမည့် ပမာဏကို ရိုက်ထည့်ပါ။' },
                                { type: 'number', min: 1, message: 'ပမာဏသည် 0 ထက်ပိုရမည်။' }
                            ]}
                        >
                            <InputNumber
                                placeholder="လှူဒါန်းမည့် ပမာဏ"
                                min={0}
                                addonAfter="MMK"
                                size="large"
                                className=" w-full !font-poppins"
                                controls={false}
                                formatter={(value) => value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || ''}
                            />
                        </Form.Item>
                    </Form>
                </div>
            </Drawer >
        </>
    )
}

